import {
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  createStyles,
  makeStyles,
  styled,
} from "@material-ui/core";
import { useLocation, useNavigate } from "react-router-dom";
import { getCurrentTab } from "../links";
import {
  type NavBadge,
  type NavBadgeTone,
  useVisibleNavItems,
} from "./epocus-nav-items";
import {
  EPOCUS_AMBER,
  EPOCUS_BORDER,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_INACTIVE,
  EPOCUS_RED,
  EPOCUS_WHITE,
  rem,
} from "./epocus-palette";
import { epocusTheme } from "./epocus-theme-provider";

const Nav = styled(BottomNavigation)({
  borderTop: `1px solid ${EPOCUS_BORDER}`,
  height: 64,
  paddingBottom: "env(safe-area-inset-bottom)",
  // Sidebar replaces bottom nav on tablet+
  [epocusTheme.breakpoints.up("sm")]: {
    display: "none",
  },
});

// Minimum 44×44pt touch target — WCAG + bedside-use requirement (PNE-164)
const NavAction = styled(BottomNavigationAction)({
  minWidth: 44,
  minHeight: 44,
  padding: "6px 0",
  position: "relative",
  color: EPOCUS_INACTIVE,
  "& .MuiBottomNavigationAction-label": {
    fontSize: rem(14),
    marginTop: 2,
  },
  "&.Mui-selected": {
    color: EPOCUS_ICON_ACTIVE,
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "25%",
      right: "25%",
      height: 3,
      backgroundColor: EPOCUS_ICON_ACTIVE,
      borderRadius: "0 0 3px 3px",
    },
  },
});

const useBadgeStyles = makeStyles(
  createStyles<NavBadgeTone, unknown>({
    red: { backgroundColor: EPOCUS_RED, color: EPOCUS_WHITE },
    amber: { backgroundColor: EPOCUS_AMBER, color: EPOCUS_WHITE },
  }),
);

export const EpocusBottomNav: FC<{
  reviewCount?: number;
  referralCount?: number;
  incompleteCount?: number;
}> = ({ reviewCount = 0, referralCount = 0, incompleteCount = 0 }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const navItems = useVisibleNavItems();
  const badgeClasses = useBadgeStyles();

  const badgeCounts: Record<NavBadge["countKey"], number> = {
    review: reviewCount,
    referral: referralCount,
    incomplete: incompleteCount,
  };

  return (
    <Nav
      value={getCurrentTab(pathname)}
      onChange={(_, value: string) => navigate(value)}
      showLabels
    >
      {navItems.map(({ shortLabel, path, icon: Icon, badge }) => {
        const href = path();
        return (
          <NavAction
            key={href}
            label={shortLabel}
            value={href}
            icon={
              badge ? (
                <Badge
                  badgeContent={badgeCounts[badge.countKey]}
                  overlap="rectangular"
                  classes={{ badge: badgeClasses[badge.tone] }}
                >
                  <Icon />
                </Badge>
              ) : (
                <Icon />
              )
            }
          />
        );
      })}
    </Nav>
  );
};
